'use client';

import React from 'react';
import { twMerge } from 'tailwind-merge';

export interface ButtonProps {
  children: React.ReactNode;
  variant?: 'contained' | 'outlined' | 'text';
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  size?: 'small' | 'medium' | 'large';
  disabled?: boolean;
  fullWidth?: boolean;
  href?: string;
  target?: string;
  rel?: string;
  type?: 'button' | 'submit' | 'reset';
  loading?: boolean;
  loadingText?: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  className?: string;
  component?: React.ElementType;
}

// Simple spinner component
const Spinner: React.FC<{ size?: 'small' | 'medium' | 'large' }> = ({ size = 'medium' }) => {
  const sizeClasses = {
    small: 'min-w-4 w-4 h-4',
    medium: 'min-w-5 w-5 h-5',
    large: 'min-w-6 w-6 h-6',
  }[size];

  return (
    <svg
      className={twMerge('animate-spin', sizeClasses)}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
};

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'contained',
  color = 'primary',
  size = 'medium',
  disabled = false,
  fullWidth = false,
  href,
  target,
  rel,
  type = 'button',
  loading = false,
  loadingText,
  onClick,
  className = '',
  component: Component,
}) => {
  // Determine if button should be disabled
  const isDisabled = disabled || loading;

  // Base classes
  const baseClasses = twMerge(
    'px-2 inline-flex items-center justify-center rounded transition-colors duration-200',
    'font-medium no-underline outline-none relative',
    isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
    fullWidth ? 'w-full' : 'w-auto'
  );

  // Size classes
  const sizeClasses = {
    small: 'py-1.5 px-3 text-sm',
    medium: 'py-2 px-4 text-base',
    large: 'py-2.5 px-5 text-lg',
  }[size];

  // Color classes
  const colorClasses = {
    primary: {
      contained: 'bg-primary text-white hover:bg-primary-dark',
      outlined:
        'bg-transparent text-primary !border-1 !border-primary hover:bg-primary hover:text-white',
      text: 'bg-transparent text-primary hover:text-primary-dark',
    },
    secondary: {
      contained: 'bg-secondary text-white hover:bg-secondary-dark',
      outlined: 'bg-transparent text-secondary border border-secondary hover:bg-secondary/50',
      text: 'bg-transparent text-secondary hover:text-secondary-dark',
    },
    error: {
      contained: 'bg-error text-white hover:bg-error-dark',
      outlined: 'bg-transparent text-error border border-error hover:bg-error/50',
      text: 'bg-transparent text-error hover:text-error-dark',
    },
    warning: {
      contained: 'bg-warning text-white hover:bg-warning-dark',
      outlined: 'bg-transparent text-warning border border-warning hover:bg-warning/50',
      text: 'bg-transparent text-warning hover:text-warning-dark',
    },
    info: {
      contained: 'bg-info text-white hover:bg-info-dark',
      outlined: 'bg-transparent text-info border border-info hover:bg-info/50',
      text: 'bg-transparent text-info hover:text-info-dark',
    },
    success: {
      contained: 'bg-success text-white hover:bg-success-dark',
      outlined: 'bg-transparent text-success border border-success hover:bg-success/50',
      text: 'bg-transparent text-success hover:text-success-dark',
    },
  }[color][variant];

  // Adjust padding for text variant
  const textVariantClasses = variant === 'text' ? 'py-1 px-2' : '';

  // Combine all classes
  const classes = twMerge(baseClasses, sizeClasses, colorClasses, textVariantClasses, className);

  // Button content with loading state - using position absolute to maintain width
  const buttonContent = (
    <>
      {/* Original content - hidden when loading but preserves width */}
      <span className={loading ? 'invisible' : 'visible flex items-center'}>{children}</span>

      {/* Loading spinner - positioned absolutely to not affect layout */}
      {loading && (
        <div className="absolute px-2 left-0 right-0 inset-0 flex items-center justify-center">
          <div className="flex items-center gap-2 w-full">
            <Spinner size={size} />
            {loadingText && (
              <span className="inline-block overflow-hidden text-ellipsis">{loadingText}</span>
            )}
          </div>
        </div>
      )}
    </>
  );

  // If a custom component is provided, use it
  if (Component) {
    return (
      <Component
        href={href}
        target={target}
        rel={rel}
        type={type}
        onClick={isDisabled ? undefined : onClick}
        disabled={isDisabled}
        className={classes}
      >
        {buttonContent}
      </Component>
    );
  }

  // If href is provided, render an anchor tag
  if (href) {
    return (
      <a href={href} target={target} rel={rel} className={classes}>
        {buttonContent}
      </a>
    );
  }

  // Otherwise, render a button
  return (
    <button
      type={type}
      onClick={isDisabled ? undefined : onClick}
      disabled={isDisabled}
      className={classes}
    >
      {buttonContent}
    </button>
  );
};

export default Button;
